import {
  ProductAnalyticsEvent,
  ProductAnalyticsEventV2
} from '@dadohr/shared/domainTypes/productAnalytics';
import { ThemeOverrides } from '@dadohr/shared/domainTypes/theme';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { tsFromMillis } from '../../services/time';
import { AppThemeProviderWithOptionalCustomizations } from '../../themes/provider';
import { LocalUpdateProvider } from '../v2/featutres/Experience/services/localUpdateProvider';

export const CURRENT_DATE_FN = () => Date.now();
const NOOP = () => undefined;

export type StandaloneAppRouting = {
  toMoment: {
    href?: (accessToken: string) => string;
    onClick: (accessToken: string) => void;
  };
  toTask: {
    href?: (accessToken: string, taskId: string) => string;
    onClick: (accessToken: string, taskId: string) => void;
  };
  toOverview: {
    href?: (accessToken: string) => string;
    onClick: (accessToken: string) => void;
  };
};

const NOOP_ROUTING: StandaloneAppRouting = {
  toMoment: {
    onClick: NOOP
  },
  toTask: {
    onClick: NOOP
  },
  toOverview: {
    onClick: NOOP
  }
};

const StandaloneAppContext = React.createContext<{
  timeFn: () => number;
  mode: 'PREVIEW' | 'LIVE';
  routing: StandaloneAppRouting;
  theming: {
    setOverrides: (ov: ThemeOverrides | null) => void;
  };
  productAnalytics: {
    trackEvent: (accessToken: string, event: ProductAnalyticsEvent) => void;
    trackEventV2: (
      userAccessToken: string,
      event: ProductAnalyticsEventV2
    ) => void;
  };
}>({
  timeFn: CURRENT_DATE_FN,
  mode: 'PREVIEW',
  routing: NOOP_ROUTING,
  theming: {
    setOverrides: NOOP
  },
  productAnalytics: {
    trackEvent: NOOP,
    trackEventV2: NOOP
  }
});

export const StandaloneAppContextProvider: React.FC<{
  timeFn?: () => number;
  mode?: 'PREVIEW' | 'LIVE';
  routing?: StandaloneAppRouting;
  initialThemeOverrides?: ThemeOverrides | null;
  trackProductAnalyticsEvent?: (
    accessToken: string,
    event: ProductAnalyticsEvent
  ) => void;
  trackProductAnalyticsEventV2?: (
    userAccessToken: string,
    event: ProductAnalyticsEventV2
  ) => void;
}> = ({
  timeFn = CURRENT_DATE_FN,
  mode = 'PREVIEW',
  routing = NOOP_ROUTING,
  initialThemeOverrides,
  trackProductAnalyticsEvent = NOOP,
  trackProductAnalyticsEventV2 = NOOP,
  children
}) => {
  const [themeOverrides, setThemeOverrides] = useState<ThemeOverrides | null>(
    initialThemeOverrides || null
  );
  const value = useMemo(
    () => ({
      timeFn,
      mode,
      routing,
      theming: {
        setOverrides: (ov: ThemeOverrides | null) => {
          // need to be really careful here that we don't create new themes all the time
          // when object equality changes. The way MUI and Emotion deals with themes
          // would lead to us just trashing the browser with new stylesheets
          setThemeOverrides((t) => {
            return isEqual(t, ov) ? t : ov;
          });
        }
      },
      productAnalytics: {
        trackEvent: trackProductAnalyticsEvent,
        trackEventV2: trackProductAnalyticsEventV2
      }
    }),
    [
      timeFn,
      mode,
      routing,
      trackProductAnalyticsEvent,
      trackProductAnalyticsEventV2
    ]
  );
  return (
    <StandaloneAppContext.Provider value={value}>
      <AppThemeProviderWithOptionalCustomizations
        theme={themeOverrides}
        variant="EMPLOYEE_APP"
      >
        <LocalUpdateProvider>{children}</LocalUpdateProvider>
      </AppThemeProviderWithOptionalCustomizations>
    </StandaloneAppContext.Provider>
  );
};

export const useStandaloneAppContext = () => {
  const value = useContext(StandaloneAppContext);
  return useMemo(() => {
    return {
      time: {
        date: () => value.timeFn(),
        moment: () => moment(value.timeFn()),
        ts: () => tsFromMillis(value.timeFn())
      },
      routing: value.routing,
      mode: value.mode,
      theming: value.theming,
      productAnalytics: value.productAnalytics
    };
  }, [value]);
};

export const useStandaloneAppThemeOverride = (
  themeOverrides: ThemeOverrides | null
) => {
  const { theming } = useStandaloneAppContext();
  useEffect(() => {
    if (themeOverrides) {
      theming.setOverrides(themeOverrides);
    }
  }, [theming, themeOverrides]);
};
